import classNames from 'classnames';
import { inject, observer } from 'mobx-react';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  FormattedMessage,
  IntlProvider,
  injectIntl,
  intlShape,
} from 'react-intl';
import {
  Alert,
  Button,
  Col,
  Modal,
  ModalBody,
  ModalHeader,
  Row,
} from 'reactstrap';

import {
  mapPreferredLocalesToSupportedLanguage,
  mapToSupportedLanguage,
} from '../../../i18n/languageLocales';
import {
  loadLocaleData,
  loadLocaleDataDynamically,
  loadMessages,
  loadMessagesDynamically,
  loadStaticMessages,
  loadStaticMessagesDynamically,
} from '../../../i18n/localeLoader';
import { modelOf } from '../../../prop-types';
import LanguageStore from '../../../store/LanguageStore';
import LocalizationStore from '../../../store/LocalizationStore';
import TranslationStore from '../../../store/TranslationStore';
import { blockScroll, unblockScroll } from '../../../util/dom';
import {
  domainMatchesUrl,
  transformDomainToUrl,
  transformExternalLink,
} from '../../../util/url';

const SITE_CHOSEN_PARAMETER = '?siteChosen=1';

const loadTranslationsForLocale = (locale) => {
  const loaderLocale = resolveLoaderLocale(locale);

  if (!loaderLocale) {
    return Promise.resolve({
      loaderLocale: null,
      messages: {},
      staticMessages: {},
    });
  }

  return Promise.all([
    loadLocaleDataDynamically(loaderLocale),
    loadMessagesDynamically(loaderLocale),
    loadStaticMessagesDynamically(loaderLocale),
  ]).then(([localeData, messages, staticMessages]) => {
    const ensureLocaleData = () => {
      if (localeData && localeData.default) {
        return import('react-intl').then(({ addLocaleData }) => {
          addLocaleData(localeData.default);
        });
      }

      return Promise.resolve();
    };

    return ensureLocaleData().then(() => ({
      loaderLocale,
      messages,
      staticMessages,
    }));
  });
};

const normaliseLocale = (locale) => {
  if (!locale || typeof locale !== 'string') {
    return null;
  }

  return locale.toLowerCase();
};

const resolveLoaderLocale = (locale) => {
  if (!locale || typeof locale !== 'string') {
    return null;
  }

  const lowerCased = locale.toLowerCase();
  const parts = lowerCased.split('-');
  const base = parts[0];

  const candidates = [lowerCased, base].filter(Boolean);

  for (const candidate of candidates) {
    if (
      loadLocaleData[candidate] &&
      loadMessages[candidate] &&
      loadStaticMessages[candidate]
    ) {
      return candidate;
    }
  }

  return null;
};

const RedirectModal = (props) => {
  const { localizationStore, languageStore, translationStore, intl } = props;

  const isSSR = typeof window === 'undefined' || window.isSSR;

  const [countryTranslations, setCountryTranslations] = useState({});
  const [mainTranslations, setMainTranslations] = useState({});
  const [translationsLoaded, setTranslationsLoaded] = useState(isSSR);

  const browserLocale = useMemo(() => {
    if (typeof navigator === 'undefined') {
      return null;
    }

    const preferredLocales =
      Array.isArray(navigator.languages) && navigator.languages.length
        ? navigator.languages
        : [navigator.language];

    return mapPreferredLocalesToSupportedLanguage(preferredLocales);
  }, []);

  const storeLocale = mapToSupportedLanguage(languageStore.activeLocale);
  const intlLocale = mapToSupportedLanguage(intl.locale);
  const intlDefaultLocale = mapToSupportedLanguage(intl.defaultLocale);

  const targetLocale =
    browserLocale || storeLocale || intlLocale || intlDefaultLocale || 'en';
  const normalisedTargetLocale = normaliseLocale(targetLocale);

  const initializationRef = useRef(false);

  const hasSiteSelection =
    typeof window !== 'undefined' && localizationStore.getLocalStorage();

  const suggestionEnabled =
    localizationStore.suggestionEnabled && !hasSiteSelection;
  const isModalOpen = localizationStore.localizationModal.isOpen;

  useEffect(() => {
    if (!suggestionEnabled) {
      initializationRef.current = false;
      return undefined;
    }

    if (!initializationRef.current) {
      initializationRef.current = true;
      localizationStore.localizationModal.toggle();
    }

    return undefined;
  }, [suggestionEnabled, localizationStore.localizationModal]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    if (isModalOpen) {
      blockScroll();
      return () => {
        unblockScroll();
      };
    }

    unblockScroll();
    return undefined;
  }, [isModalOpen]);

  useEffect(() => {
    return () => {
      if (typeof window !== 'undefined') {
        unblockScroll();
      }
    };
  }, []);

  useEffect(() => {
    if (!suggestionEnabled) {
      if (!isSSR) {
        setTranslationsLoaded(false);
      }
      return undefined;
    }

    if (isSSR) {
      return undefined;
    }

    const preferredLocales = Array.from(
      new Set([targetLocale, normalisedTargetLocale, 'en'].filter(Boolean))
    );

    const seenRequests = new Set();
    const loadRequests = [];

    preferredLocales.forEach((locale) => {
      if (!locale) {
        return;
      }

      const loaderLocale = resolveLoaderLocale(locale);
      const keysToCheck = [
        locale,
        normaliseLocale(locale),
        loaderLocale,
        normaliseLocale(loaderLocale),
      ].filter(Boolean);

      const hasMain = keysToCheck.some((key) => key && mainTranslations[key]);
      const hasCountry = keysToCheck.some(
        (key) => key && countryTranslations[key]
      );

      if (hasMain && hasCountry) {
        return;
      }

      const requestKey = `${locale}|${loaderLocale || ''}`;
      if (seenRequests.has(requestKey)) {
        return;
      }

      seenRequests.add(requestKey);
      loadRequests.push({ requestedLocale: locale, loaderLocale });
    });

    if (loadRequests.length === 0) {
      setTranslationsLoaded(true);
      return undefined;
    }

    let cancelled = false;
    setTranslationsLoaded(false);

    Promise.all(
      loadRequests.map(({ requestedLocale }) =>
        loadTranslationsForLocale(requestedLocale).then(
          ({ loaderLocale, messages, staticMessages }) => ({
            requestedLocale,
            loaderLocale,
            messages,
            staticMessages,
          })
        )
      )
    )
      .then((results) => {
        if (cancelled) {
          return;
        }

        setMainTranslations((prev) => {
          const updated = { ...prev };

          results.forEach(({ requestedLocale, loaderLocale, messages }) => {
            const keys = [
              requestedLocale,
              normaliseLocale(requestedLocale),
              loaderLocale,
              normaliseLocale(loaderLocale),
            ].filter(Boolean);

            keys.forEach((key) => {
              if (key) {
                updated[key] = messages;
              }
            });
          });

          return updated;
        });

        setCountryTranslations((prev) => {
          const updated = { ...prev };

          results.forEach(
            ({ requestedLocale, loaderLocale, staticMessages }) => {
              const keys = [
                requestedLocale,
                normaliseLocale(requestedLocale),
                loaderLocale,
                normaliseLocale(loaderLocale),
              ].filter(Boolean);

              keys.forEach((key) => {
                if (key) {
                  updated[key] = staticMessages;
                }
              });
            }
          );

          return updated;
        });
      })
      .finally(() => {
        if (!cancelled) {
          setTranslationsLoaded(true);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [
    suggestionEnabled,
    targetLocale,
    normalisedTargetLocale,
    countryTranslations,
    mainTranslations,
    isSSR,
  ]);

  if (!suggestionEnabled) {
    return null;
  }

  const handleSuggestionIgnore = () => {
    localizationStore.setLocalStorage();
    localizationStore.localizationModal.toggle();

    if (typeof window !== 'undefined') {
      unblockScroll();
    }
  };

  const redirectTo = (url) => {
    handleSuggestionIgnore();

    if (typeof window !== 'undefined') {
      window.location.assign(
        transformExternalLink(url + SITE_CHOSEN_PARAMETER)
      );
    }
  };

  const hasShipping = localizationStore.sites.some(
    (site) => site.shipsToCountry === true
  );

  const currentSite =
    typeof window !== 'undefined' ? `${window.location.origin}/` : '';

  const sites =
    localizationStore.sites.length === 0
      ? null
      : localizationStore.sites.map((site) => {
          const isCurrent = domainMatchesUrl(site.domain, currentSite);

          return (
            <Alert
              key={site.domain}
              className={classNames('RedirectModal__frame', {
                'RedirectModal__frame-suggested': site.recommendedForCountry,
              })}
            >
              <Row>
                <Col className="RedirectModal__site" xs={12}>
                  <div className="RedirectModal__site-name">{site.domain}</div>
                </Col>
                <Col xs={12} sm={7}>
                  {site.recommendedForCountry && (
                    <div className="RedirectModal__site-suggested">
                      <FormattedMessage
                        id="localization.isSuggestedSite"
                        defaultMessage="We recommend using this online store."
                      />
                    </div>
                  )}
                  {site.shipsToCountry && (
                    <div className="RedirectModal__site-shippingPossible">
                      <FormattedMessage
                        id="localization.shippingPossible"
                        defaultMessage="Shipping is possible to your country."
                      />
                    </div>
                  )}
                </Col>
                <Col xs={12} sm={5} className="RedirectModal__button">
                  <div className="RedirectModal__button-div">
                    <Button
                      color={
                        site.recommendedForCountry ? 'primary' : 'secondary'
                      }
                      block
                      className="RedirectModal__button--accept"
                      role="link"
                      onClick={() =>
                        redirectTo(transformDomainToUrl(site.domain))
                      }
                    >
                      <div className="RedirectModal__accept-text">
                        {isCurrent ? (
                          <FormattedMessage
                            id="localization.doNotRedirect"
                            defaultMessage="Stay on this online store"
                          />
                        ) : (
                          <FormattedMessage
                            id="localization.moveToSite"
                            defaultMessage="Go to the online store"
                          />
                        )}
                      </div>
                    </Button>
                  </div>
                </Col>
              </Row>
            </Alert>
          );
        });

  if (!sites) {
    return null;
  }

  if (!translationsLoaded) {
    return null;
  }

  const localeCandidates = [targetLocale, normalisedTargetLocale, 'en'].filter(
    Boolean
  );

  let countryTranslation = localizationStore.countryCode;

  if (localizationStore.countryCode) {
    for (const candidate of localeCandidates) {
      const normalisedCandidate = normaliseLocale(candidate);

      const translations =
        countryTranslations[candidate] ||
        (normalisedCandidate && countryTranslations[normalisedCandidate]);

      if (translations && translations[localizationStore.countryCode]) {
        countryTranslation = translations[localizationStore.countryCode];
        break;
      }
    }
  }

  const normalizedCountryTranslation =
    typeof countryTranslation === 'string'
      ? countryTranslation.trim() || localizationStore.countryCode
      : countryTranslation || localizationStore.countryCode;

  const localeForMessages =
    localeCandidates.find((candidate) => {
      const normalisedCandidate = normaliseLocale(candidate);

      return (
        mainTranslations[candidate] ||
        (normalisedCandidate && mainTranslations[normalisedCandidate])
      );
    }) || 'en';

  const normalisedLocaleForMessages = normaliseLocale(localeForMessages);

  const baseMessages =
    mainTranslations[localeForMessages] ||
    (normalisedLocaleForMessages &&
      mainTranslations[normalisedLocaleForMessages]) ||
    {};

  const countryMessages =
    countryTranslations[localeForMessages] ||
    (normalisedLocaleForMessages &&
      countryTranslations[normalisedLocaleForMessages]) ||
    {};

  const storeLocaleMatches =
    (storeLocale && storeLocale === normalisedLocaleForMessages) ||
    (intlLocale && intlLocale === normalisedLocaleForMessages) ||
    (intlDefaultLocale && intlDefaultLocale === normalisedLocaleForMessages);

  const storeMessages =
    storeLocaleMatches && translationStore.messages
      ? translationStore.messages
      : {};

  const messages = {
    ...baseMessages,
    ...storeMessages,
    ...countryMessages,
  };

  const modal = localizationStore.localizationModal;

  return (
    <IntlProvider locale={localeForMessages || 'en'} messages={messages}>
      <Modal
        className="RedirectModal"
        size="md"
        isOpen={modal.isOpen}
        toggle={handleSuggestionIgnore}
        backdrop="static"
        keyboard={false}
      >
        <ModalHeader toggle={handleSuggestionIgnore}>
          {localizationStore.countryCode && (
            <Row className="RedirectModal__localization-header">
              <Col xs={12} className="RedirectModal__localization-phrase">
                <div className="RedirectModal__localization-phrase-country">
                  <FormattedMessage
                    id="localization.yourLocation"
                    defaultMessage="We detected you are in {country}"
                    values={{
                      country: normalizedCountryTranslation,
                    }}
                  />
                </div>
              </Col>
            </Row>
          )}
          {!localizationStore.countryCode && (
            <FormattedMessage
              id="localization.noSuggestion"
              defaultMessage="We do not recognize where you are. Please check out our available online stores."
            />
          )}
        </ModalHeader>
        <ModalBody>
          <div className="RedirectModal__body">
            {!hasShipping && !!localizationStore.countryCode && (
              <div className="RedirectModal__noShipping">
                <FormattedMessage
                  id="localization.noShipping"
                  defaultMessage="Please check out our available online stores as unfortunately we do not deliver to the detected country."
                />
              </div>
            )}
            <div className="RedirectModal__sites">
              <div className="RedirectModal__sites-row">{sites}</div>
            </div>
          </div>
        </ModalBody>
      </Modal>
    </IntlProvider>
  );
};

RedirectModal.propTypes = {
  localizationStore: modelOf(LocalizationStore),
  languageStore: modelOf(LanguageStore),
  translationStore: modelOf(TranslationStore),
  intl: intlShape.isRequired,
};

export default injectIntl(
  inject(
    'localizationStore',
    'languageStore',
    'translationStore'
  )(observer(RedirectModal))
);
